import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPlus, faTrash, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { 
  adicionarAvatar, 
  removerAvatar, 
  atualizarAvatar,
  toggleVisibilidadeAvatar 
} from "../actions/avatarsActions";
import "~/styles/AvatarPanel.css";

export const AvatarPanel = () => {
  const avatars = useSelector((state) => state.avatars);
  const executando = useSelector((state) => state.nodes.isExecuting);
  const dispatch = useDispatch();
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Limpa a posição do mouse quando a execução termina
  useEffect(() => {
    if (!executando) {
      setMousePosition({ x: 0, y: 0 });
    }
  }, [executando]);

  const handleAddAvatar = () => {
    if (executando) {
      alert("Não é possível adicionar avatars durante a execução do fluxo");
      return;
    }
    dispatch(adicionarAvatar());
  };
  
  const handleRemoveAvatar = (index) => {
    if (executando) {
      alert("Não é possível remover avatars durante a execução do fluxo");
      return;
    }
    dispatch(removerAvatar(index));
  };
  
  const handleChange = (index, campo, valor) => {
    if (executando) {
      alert("Não é possível editar avatars durante a execução do fluxo");
      return;
    }
    dispatch(atualizarAvatar(index, campo, valor));
  };
  
  const handleToggleVisibilidade = (index) => {
    if (executando) {
      alert("Não é possível alterar a visibilidade dos avatars durante a execução do fluxo");
      return;
    }
    const avatar = avatars[index];
    dispatch(toggleVisibilidadeAvatar(index, !avatar.visivelInicial));
    // Também atualiza o campo visivelInicial
    dispatch(atualizarAvatar(index, "visivelInicial", !avatar.visivelInicial));
  };

  const handleImageUpload = (index, event) => {
    if (executando) {
      alert("Não é possível alterar imagens durante a execução do fluxo");
      return;
    }
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleChange(index, "imagem", e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMouseMove = (e) => {
    if (executando) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseLeave = () => {
    // Limpa a posição do mouse quando sai da área do painel
    setMousePosition({ x: 0, y: 0 });
  };

  const handlePlaceholderClick = (index) => {
    if (executando) {
      alert("Não é possível alterar imagens durante a execução do fluxo");
      return;
    }
    // Encontra o input de arquivo correspondente e simula um clique
    const fileInput = document.getElementById(`file-input-${index}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div 
      className="avatar-panel" 
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Camada de bloqueio durante execução */}
      {executando && (
        <>
          <div className="avatar-execution-overlay" />
          {mousePosition.x > 0 && mousePosition.y > 0 && (
            <div 
              className="avatar-tooltip" 
              style={{ 
                left: mousePosition.x + 10, 
                top: mousePosition.y - 25 
              }}
            >
              Fluxo em execução - Avatares bloqueados
            </div>
          )}
        </>
      )}
      
      <div className="avatar-header">
        <h3 className="avatar-title">Avatares</h3>
        <button 
          className="add-avatar-btn" 
          onClick={handleAddAvatar}
          disabled={executando}
          title={executando ? "Não é possível adicionar avatars durante a execução" : "Adicionar avatar"}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
      
      <div className="avatar-container">
        {avatars.map((avatar, index) => (
          <div key={index} className="avatar-item">
            {/* Aviso se não tem nome */}
            {!avatar.nome && (
              <div className="avatar-warning">
                DEFINA O NOME DO AVATAR PARA USO
              </div>
            )}
            
            <div className="avatar-content">
              {/* Preview do avatar */}
              <div className="avatar-preview">
                {avatar.imagem ? (
                  <img 
                    src={avatar.imagem} 
                    alt={avatar.nome || "Avatar"} 
                    style={{ 
                      width: `${Math.min(avatar.tamanho, 40)}px`, 
                      height: `${Math.min(avatar.tamanho, 40)}px`,
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div 
                    className="avatar-placeholder clickable" 
                    onClick={() => handlePlaceholderClick(index)}
                    title={executando ? "Não é possível alterar durante a execução" : "Clique para adicionar imagem"}
                  >
                    <FontAwesomeIcon icon={faUser} size="2x" />
                    <div className="plus-icon">
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                  </div>
                )}
              </div>

              {/* Controles do avatar */}
              <div className="avatar-controls">
                <div className="avatar-field">
                  <label>Nome:</label>
                  <input
                    type="text"
                    className="avatar-input"
                    value={avatar.nome}
                    onChange={(e) => handleChange(index, "nome", e.target.value)}
                    placeholder="Nome do avatar"
                    disabled={executando}
                    title={executando ? "Não é possível editar durante a execução" : ""}
                  />
                </div>

                <div className="avatar-field">
                  <label>Imagem:</label>
                  <input
                    id={`file-input-${index}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(index, e)}
                    className="avatar-file-input"
                    disabled={executando}
                    title={executando ? "Não é possível alterar imagem durante a execução" : ""}
                  />
                </div>

                <div className="avatar-field">
                  <label>Tamanho:</label>
                  <input
                    type="range"
                    min="20"
                    max="200"
                    value={avatar.tamanho}
                    onChange={(e) => handleChange(index, "tamanho", parseInt(e.target.value))}
                    className="avatar-slider"
                    disabled={executando}
                    title={executando ? "Não é possível alterar tamanho durante a execução" : ""}
                  />
                  <span className="size-display">{avatar.tamanho}px</span>
                </div>

                <div className="avatar-field">
                  <label>Rotação:</label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={avatar.rotacao || 0}
                    onChange={(e) => handleChange(index, "rotacao", parseInt(e.target.value))}
                    className="avatar-slider"
                    disabled={executando}
                    title={executando ? "Não é possível alterar rotação durante a execução" : ""}
                  />
                  <span className="size-display">{avatar.rotacao || 0}°</span>
                </div>

                <div className="avatar-actions">
                  <button 
                    className={`visibility-btn ${avatar.visivelInicial ? 'visible' : 'hidden'}`}
                    onClick={() => handleToggleVisibilidade(index)}
                    disabled={executando}
                    title={executando ? "Não é possível alterar visibilidade durante a execução" : (avatar.visivelInicial ? "Ocultar avatar" : "Mostrar avatar")}
                  >
                    <FontAwesomeIcon icon={avatar.visivelInicial ? faEye : faEyeSlash} />
                  </button>
                  
                  <button 
                    className="delete-btn"
                    onClick={() => handleRemoveAvatar(index)}
                    disabled={executando}
                    title={executando ? "Não é possível remover durante a execução" : "Remover avatar"}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>

                  {/* Ícone de arrastar só aparece se tem nome e não está executando */}
                  {avatar.nome && !executando && (
                    <div
                      className="drag-handle"
                      draggable
                      onDragStart={(e) => {
                        setDraggingIndex(index);
                        e.dataTransfer.setData('application/avatar', JSON.stringify({...avatar, index}));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onDragEnd={() => {
                        setDraggingIndex(null);
                      }}
                      style={{ cursor: draggingIndex === index ? 'grabbing' : 'grab' }}
                      title="Arrastar para usar no fluxograma"
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {avatars.length === 0 && (
          <div className="empty-avatars">
            <p>Nenhum avatar criado ainda.</p>
            <p>Clique no + para adicionar um avatar.</p>
          </div>
        )}
      </div>
    </div>
  );
};
